// Typed client for the Vetproof REST API (/api/* → Netlify Function).
import type {
  EmergencyCard, Me, NewNoteInput, NewPetInput, NewVisitInput, OwnerSummary, PetRecord, PetSummary, RegisterResult, VerifyResult,
} from "../shared/types";
import { accessToken } from "./auth";

export class ApiError extends Error {
  constructor(public status: number, message: string, public code?: string) {
    super(message);
  }
}

async function request<T>(method: string, path: string, body?: unknown): Promise<T> {
  const token = await accessToken();
  const res = await fetch(`/api${path}`, {
    method,
    headers: {
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
      ...(body !== undefined ? { "Content-Type": "application/json" } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => null);
  if (!res.ok) throw new ApiError(res.status, data?.error ?? `Request failed (${res.status})`, data?.code);
  return data as T;
}

export interface SealResult { seq: number; hash: string; prev_hash: string }
export interface TamperResult { visitId: number; attempts: { label: string; blocked: boolean; message: string }[]; chainStillValid: boolean }

export const api = {
  me: () => request<Me>("GET", "/me"),
  pets: (q?: string) => request<PetSummary[]>("GET", `/pets${q ? `?q=${encodeURIComponent(q)}` : ""}`),
  registerPet: (input: NewPetInput) => request<RegisterResult>("POST", "/pets", input),
  owners: (q: string) => request<OwnerSummary[]>("GET", `/owners?q=${encodeURIComponent(q)}`),
  byChip: (chip: string) => request<{ id: number }>("GET", `/pets/by-chip/${encodeURIComponent(chip)}`),
  record: (id: number) => request<PetRecord>("GET", `/pets/${id}`),
  verify: (id: number) => request<VerifyResult>("GET", `/pets/${id}/verify`),
  addVisit: (id: number, input: NewVisitInput) => request<SealResult & { visit_id: number; notes: SealResult[] }>("POST", `/pets/${id}/visits`, input),
  addNote: (id: number, input: NewNoteInput) => request<SealResult & { note_id: number }>("POST", `/pets/${id}/notes`, input),
  tamperTest: (id: number) => request<TamperResult>("POST", `/pets/${id}/tamper-test`),
  emergency: (chip: string) => request<EmergencyCard>("GET", `/emergency/${encodeURIComponent(chip)}`),
  translate: (lang: string, strings: string[]) => request<{ translations: string[] }>("POST", "/ai/translate", { lang, strings }),
  brief: (id: number) => request<{ html: string }>("POST", `/pets/${id}/ai/brief`),
};
